// Config panel for the "if_condition" condition block
// Evaluates a single variable comparison and branches true/false
import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  Alert
} from '@mui/material';

const OPERATORS = [
  { value: 'eq', label: '== (equals)', needsValue: true },
  { value: 'neq', label: '!= (not equals)', needsValue: true },
  { value: 'gt', label: '> (greater than)', needsValue: true },
  { value: 'gte', label: '>= (greater or equal)', needsValue: true },
  { value: 'lt', label: '< (less than)', needsValue: true },
  { value: 'lte', label: '<= (less or equal)', needsValue: true },
  { value: 'contains', label: 'contains', needsValue: true },
  { value: 'not_contains', label: 'does not contain', needsValue: true },
  { value: 'starts_with', label: 'starts with', needsValue: true },
  { value: 'ends_with', label: 'ends with', needsValue: true },
  { value: 'is_true', label: 'is true / truthy', needsValue: false },
  { value: 'is_false', label: 'is false / falsy', needsValue: false },
  { value: 'is_empty', label: 'is empty / null', needsValue: false },
  { value: 'is_not_empty', label: 'is not empty', needsValue: false },
];

const IfConditionConfig = ({ config, onChange, onSummaryChange }) => {
  const selectedOp = OPERATORS.find(o => o.value === config.operator) || OPERATORS[0];

  const handleChange = (field, value) => {
    const updated = { ...config, [field]: value };
    onChange(updated);
    const varName = updated.variable || '?';
    const op = OPERATORS.find(o => o.value === updated.operator);
    const opLabel = op?.label?.split(' ')[0] || '==';
    onSummaryChange(
      op?.needsValue !== false
        ? `${varName} ${opLabel} ${updated.compareValue || '?'}`
        : `${varName} ${opLabel}`
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Evaluates a single condition. Branches <strong>TRUE</strong> if the comparison
        passes, <strong>FALSE</strong> otherwise.
      </Typography>

      <TextField
        size="small"
        label="Variable"
        value={config.variable || ''}
        onChange={(e) => handleChange('variable', e.target.value)}
        required
        helperText='Use {{varName}} syntax, e.g. "{{internalNumber}}" or "{{api.statusCode}}"'
        placeholder="e.g. {{internalNumber}}"
      />

      <FormControl fullWidth size="small">
        <InputLabel>Operator</InputLabel>
        <Select
          value={config.operator || 'eq'}
          onChange={(e) => handleChange('operator', e.target.value)}
          label="Operator"
        >
          {OPERATORS.map(op => (
            <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
          ))}
        </Select>
        <FormHelperText>How to compare the variable value</FormHelperText>
      </FormControl>

      {selectedOp.needsValue !== false && (
        <TextField
          size="small"
          label="Compare Value"
          value={config.compareValue ?? ''}
          onChange={(e) => handleChange('compareValue', e.target.value)}
          helperText='The value to compare against. Can also use {{varName}} syntax.'
          placeholder="e.g. 200 or {{threshold}}"
        />
      )}

      <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          <strong>Example:</strong> To check if an HTTP response was successful:<br />
          Variable: <code style={{ fontSize: '0.65rem' }}>{'{{api.statusCode}}'}</code><br />
          Operator: <code style={{ fontSize: '0.65rem' }}>== (equals)</code><br />
          Value: <code style={{ fontSize: '0.65rem' }}>200</code>
        </Typography>
      </Alert>
    </Box>
  );
};

export { OPERATORS };
export default IfConditionConfig;
